import { gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type { NexoyaContentLabelCategory, NexoyaContentLabelStageCounts } from '../../types';

import { STAGE_READ_CONTEXT, STAGE_READS_CONNECTION_KEY } from './stageReads';

/**
 * Content Labels v2 — how many contents sit in each of a category's four stages,
 * for every category at once (the coverage pills).
 *
 * `counts`, not each stage's `totalCount`: a stage connection also reads a page
 * of its contents, and for the unlabeled stage of a big team that page costs
 * about as much as the count itself. `counts` reads no page.
 *
 * `portfolioId` null asks for the category's whole team-wide scope; a number
 * narrows to that portfolio's parent contents (an empty portfolio answers 0s).
 *
 * The `@connection` key stores this answer's category array apart from the one
 * the category list renders (see `STAGE_READS_CONNECTION_KEY`); Apollo strips
 * the directive before the request is sent.
 */
export const CONTENT_LABEL_STAGE_COUNTS_QUERY = gql`
  query ContentLabelStageCounts($teamId: Int!, $portfolioId: Float) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${STAGE_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      contentsByAssignmentStage(portfolioId: $portfolioId) {
        counts {
          labeled
          needsDecision
          manual
          unlabeled
        }
      }
    }
  }
`;

export type StageCountsCategory = Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'> & {
  contentsByAssignmentStage: { counts: NexoyaContentLabelStageCounts };
};

export type ContentLabelStageCountsResponse = {
  listContentLabelCategories: StageCountsCategory[];
};

export type ContentLabelStageCountsVariables = {
  teamId: number;
  portfolioId: number | null;
};

/**
 * `portfolioId` is passed explicitly as `null` for the team-wide scope rather
 * than omitted, so the variables — and therefore the cache key and any mock —
 * are the same object whichever way the caller arrived at "no portfolio".
 *
 * Label mutations that move contents between stages ask this again themselves
 * (`mutationEffects.activeStageReads`); deduplication is off so that refetch is
 * always a request of its own (`STAGE_READ_CONTEXT`).
 *
 * `skip` exists because null is not "no scope" here but the whole team: a caller
 * still deciding its scope has to be able to ask for nothing at all.
 */
export function useContentLabelStageCountsQuery({
  portfolioId,
  skip = false,
}: {
  portfolioId: number | null;
  skip?: boolean;
}) {
  const { teamId } = useTeam();

  return useQuery<ContentLabelStageCountsResponse, ContentLabelStageCountsVariables>(CONTENT_LABEL_STAGE_COUNTS_QUERY, {
    variables: { teamId, portfolioId: portfolioId ?? null },
    skip: skip || !teamId,
    context: STAGE_READ_CONTEXT,
  });
}
